import { useCallback, useRef, useState } from 'react';
import type {
  Placement,
  PopoverProps,
} from '@decisiv/ui-components/lib/components/Popover/types';

/** Room kept between an open menu and the screen edge, arrow included. */
const SCREEN_MARGIN = 24;

type Layout = {
  side: 'bottom' | 'top' | 'left';
  /** Set only on the left, where a menu taller than the window must scroll. */
  maxHeight?: number;
};

// `flip` is off because the side is chosen from the menu's measured height.
// `preventOverflow` only slides the menu along its trigger, never over it:
// sideways when it is above or below, up or down when it is to the left.
// Replaces the KDS Popover default `preventOverflow`, so the viewport
// boundary is repeated.
const modifiersFor = (
  side: Layout['side'],
): PopoverProps['popperModifiers'] => ({
  flip: { enabled: false },
  preventOverflow: {
    boundariesElement: 'viewport',
    priority: side === 'left' ? ['top', 'bottom'] : ['left', 'right'],
  },
});

/** Below when the menu fits there, else above, else to the left (PD-5930 UX review). */
const layoutFor = (trigger: HTMLElement | null, menuHeight: number): Layout => {
  if (!trigger) return { side: 'bottom' };
  const { top, bottom } = trigger.getBoundingClientRect();
  if (menuHeight <= window.innerHeight - bottom - SCREEN_MARGIN) {
    return { side: 'bottom' };
  }
  if (menuHeight <= top - SCREEN_MARGIN) return { side: 'top' };
  // Sliding up or down can't bring a menu taller than the window fully on
  // screen, so cap it and let it scroll, or its top items can't be reached.
  return { side: 'left', maxHeight: window.innerHeight - 2 * SCREEN_MARGIN };
};

interface UseMenuPopoverOptions {
  /**
   * PD-5930 UX review: open the menu below its trigger, above it, or to its
   * left, whichever fits the whole menu, so it never covers the trigger. It
   * scrolls only when taller than the window. Needs `menuRef` and `menuStyle`
   * on the menu content. Opt-in, so menus
   * outside the PD-5874 pages keep the default KDS positioning.
   */
  keepTriggerVisible?: boolean;
  align?: 'start' | 'end';
}

interface SelectableItem {
  onSelect?: () => void;
}

/**
 * Wiring for a Button that toggles a Popover holding KDS Menus. Spread
 * `triggerProps` on the Button and `popoverProps` on the Popover. The
 * Popover's target is a ref, so it can't put `aria-haspopup` on the Button
 * itself; the trigger sets it to `menu` here. The Popover's content wrapper
 * gets a neutral role instead of its `dialog` default: the KDS Menu inside it
 * is already the `menu`, and a `menu` may only contain menu items or groups.
 */
export default function useMenuPopover({
  keepTriggerVisible = false,
  align = 'start',
}: UseMenuPopoverOptions = {}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);
  const [layout, setLayout] = useState<Layout>({ side: 'bottom' });

  const hide = () => {
    setVisible(false);
  };

  const toggle = () => {
    setVisible((prev) => !prev);
  };

  // The Popover mounts its content on every open, so this measures the menu
  // each time, with the page scrolled as it is now. Setting state from a ref
  // callback re-renders before paint, so the menu never shows on the wrong side.
  // `scrollHeight` is the full height even while the last open's cap applies.
  const menuRef = useCallback(
    (menu: HTMLElement | null) => {
      if (menu && keepTriggerVisible) {
        setLayout(layoutFor(buttonRef.current, menu.scrollHeight));
      }
    },
    [keepTriggerVisible],
  );

  /** Wraps each item's `onSelect` so choosing it also closes the menu. */
  const closeOnSelect = <T extends SelectableItem>(items: T[]): T[] =>
    items.map((item) => ({
      ...item,
      onSelect: () => {
        hide();
        item.onSelect?.();
      },
    }));

  return {
    visible,
    hide,
    closeOnSelect,
    /** Ref for the menu content, measured to pick its side with `keepTriggerVisible`. */
    menuRef,
    /** Style for the menu content: caps it to the window when it opens left. */
    menuStyle:
      layout.maxHeight === undefined
        ? undefined
        : { maxHeight: layout.maxHeight, overflowY: 'auto' as const },
    triggerProps: {
      ref: buttonRef,
      onClick: toggle,
      'aria-haspopup': 'menu' as const,
      'aria-expanded': visible,
    },
    popoverProps: {
      target: buttonRef,
      visible,
      onRequestHide: hide,
      placement: (layout.side === 'left'
        ? 'left-start'
        : `${layout.side}-${align}`) as Placement,
      manageEvents: false as const,
      contentRole: 'presentation',
      ...(keepTriggerVisible && {
        popperModifiers: modifiersFor(layout.side),
      }),
    },
  };
}
